import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

/**
 * Format a number for display (commas, appropriate decimals)
 * e.g. 1000000 → "1,000,000", 12.5 → "12.5", 0.077 → "0.077"
 */
export function formatNumber(value) {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  if (Number.isInteger(num)) return num.toLocaleString('en-US');
  const parts = num.toString().split('.');
  return parseInt(parts[0]).toLocaleString('en-US') + '.' + parts[1];
}

/**
 * Number input with formatting — shows formatted value when not focused,
 * raw value when editing. Stores raw number.
 */
export function FormattedNumberInput({ value, onChange, placeholder, className, ...props }) {
  const [focused, setFocused] = useState(false);
  const displayValue = focused ? (value ?? '') : formatNumber(value);
  
  return (
    <Input
      {...props}
      type={focused ? "number" : "text"}
      value={displayValue}
      placeholder={placeholder}
      className={className}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Fuzzy match helper — checks if all characters in query appear in text in order
 */
function fuzzyMatch(text, query) {
  if (!query) return true;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

/**
 * Tier filtering constants for each P2E level.
 * Mission: outcomes + deviations. Objective: projections + outcomes + deviations. Activity: all.
 */
export const TIER_FILTERS = {
  mission:   ['t3', 'delta', 'custom'],
  objective: ['t2', 't3', 'delta', 'custom'],
  activity:  ['t1', 't2', 't3', 'delta', 'custom'],
};

/**
 * MeasurementCombobox — searchable dropdown for selecting measurements from BullseyeVocabulary.
 * Groups by category, shows ●/○ status indicators, supports fuzzy search.
 * 
 * @param allowedTiers — array of tier strings to filter by (e.g. TIER_FILTERS.mission).
 *   If omitted, shows all measurements.
 */
export function MeasurementCombobox({ value, onChange, measurements = [], categories = [], allowedTiers, placeholder = "Search measurements...", className = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const tierFiltered = useMemo(() => {
    if (!allowedTiers || allowedTiers.length === 0) return measurements;
    return measurements.filter(m => allowedTiers.includes(m.tier));
  }, [measurements, allowedTiers]);

  const selectedItem = measurements.find(m => m.vocab_id === value);

  const filtered = useMemo(() => {
    if (!search) return tierFiltered;
    return tierFiltered.filter(m =>
      fuzzyMatch(m.label, search) ||
      fuzzyMatch(m.category, search) ||
      fuzzyMatch(m.dimension || '', search) ||
      fuzzyMatch(m.unit || '', search)
    );
  }, [tierFiltered, search]);

  const filteredCategories = useMemo(() => {
    const cats = [...new Set(tierFiltered.map(m => m.category))];
    return cats.filter(cat => filtered.some(m => m.category === cat));
  }, [tierFiltered, filtered]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className} ${!value ? 'text-slate-400' : 'text-slate-100'}`}>
          <span className="truncate text-left flex-1">
            {selectedItem ? (
              <span className="flex items-center gap-1.5">
                <span className={selectedItem.status === 'measured' ? 'text-emerald-400' : selectedItem.status === 'instrumented' ? 'text-slate-500' : 'text-amber-400'}>
                  {selectedItem.status === 'measured' ? '●' : selectedItem.status === 'instrumented' ? '○' : '◇'}
                </span>
                {selectedItem.label}
                <span className="text-slate-500 text-xs">{selectedItem.unit}</span>
              </span>
            ) : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 bg-slate-900 border-slate-700" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b border-slate-800 px-3">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              placeholder="Type to search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full bg-transparent py-2 text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            />
          </div>
          <CommandList className="max-h-[280px]">
            {filtered.length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">No measurements match "{search}"</div>
            )}
            {filteredCategories.map(cat => (
              <CommandGroup key={cat} heading={<span className="text-[10px] text-slate-500 uppercase font-semibold">{cat}</span>}>
                {filtered.filter(m => m.category === cat).map(m => (
                  <CommandItem
                    key={m.vocab_id}
                    value={m.vocab_id}
                    onSelect={() => { onChange(m.vocab_id, m); setOpen(false); setSearch(''); }}
                    className="flex items-center gap-2 text-slate-700 text-xs cursor-pointer">
                    <Check className={`h-3.5 w-3.5 ${value === m.vocab_id ? 'opacity-100 text-emerald-400' : 'opacity-0'}`} />
                    <span className={m.status === 'measured' ? 'text-emerald-400' : m.status === 'instrumented' ? 'text-slate-500' : 'text-amber-400'}>
                      {m.status === 'measured' ? '●' : m.status === 'instrumented' ? '○' : '◇'}
                    </span>
                    <span className="flex-1 truncate">{m.label}</span>
                    <span className="text-slate-500 text-[10px]">{m.unit}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * PositionCombobox — searchable dropdown for selecting position types from BullseyeVocabulary.
 * Groups by resolution level category.
 */
export function PositionCombobox({ value, onChange, positions = [], categories = [], placeholder = "Search positions...", className = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedItem = positions.find(p => p.vocab_id === value);

  const filtered = useMemo(() => {
    if (!search) return positions;
    return positions.filter(p =>
      fuzzyMatch(p.label, search) ||
      fuzzyMatch(p.category, search) ||
      fuzzyMatch(p.dimension || '', search)
    );
  }, [positions, search]);

  const filteredCategories = useMemo(() => {
    return categories.filter(cat => filtered.some(p => p.category === cat));
  }, [categories, filtered]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className} ${!value ? 'text-slate-400' : 'text-slate-100'}`}>
          <span className="truncate text-left flex-1">
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-700" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b border-slate-800 px-3">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              placeholder="Type to search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full bg-transparent py-2 text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            />
          </div>
          <CommandList className="max-h-[240px]">
            {filtered.length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">No positions match "{search}"</div>
            )}
            {filteredCategories.map(cat => (
              <CommandGroup key={cat} heading={<span className="text-[10px] text-slate-500 uppercase font-semibold">{cat}</span>}>
                {filtered.filter(p => p.category === cat).map(p => (
                  <CommandItem
                    key={p.vocab_id}
                    value={p.vocab_id}
                    onSelect={() => { onChange(p.vocab_id); setOpen(false); setSearch(''); }}
                    className="flex items-center gap-2 text-slate-700 text-xs cursor-pointer">
                    <Check className={`h-3.5 w-3.5 ${value === p.vocab_id ? 'opacity-100 text-emerald-400' : 'opacity-0'}`} />
                    <span className="flex-1 truncate">{p.label}</span>
                    <span className="text-slate-500 text-[10px]">{p.resolution_level}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * PositionValueCombobox — searchable input with suggestions for position values.
 * Shows known values based on position type, allows free-text "create new" entry.
 * suggestions prop: array of strings (e.g. ["BurgerFi", "LEON", "MrBeast"])
 */
export function PositionValueCombobox({ value, onChange, suggestions = [], placeholder = "Type or search...", className = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return suggestions.slice(0, 50);
    return suggestions.filter(s => fuzzyMatch(s, search)).slice(0, 50);
  }, [suggestions, search]);

  const exactMatch = suggestions.some(s => s.toLowerCase() === (search || value || '').toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className} ${!value ? 'text-slate-400' : 'text-slate-100'}`}>
          <span className="truncate text-left flex-1">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-slate-900 border-slate-700" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b border-slate-800 px-3">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              placeholder="Type to search or create..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full bg-transparent py-2 text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            />
          </div>
          <CommandList className="max-h-[200px]">
            {search && !exactMatch && (
              <CommandItem
                onSelect={() => { onChange(search); setOpen(false); setSearch(''); }}
                className="flex items-center gap-2 text-amber-600 text-xs cursor-pointer border-b border-slate-800">
                <Plus className="h-3.5 w-3.5" />
                <span>Use "{search}"</span>
              </CommandItem>
            )}
            {filtered.length === 0 && !search && (
              <div className="py-4 text-center text-sm text-slate-400">Type to search or enter a value</div>
            )}
            {filtered.length === 0 && search && exactMatch && (
              <div className="py-4 text-center text-sm text-slate-400">No additional matches</div>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {!search && (
                  <CommandItem
                    value="all"
                    onSelect={() => { onChange('all'); setOpen(false); setSearch(''); }}
                    className="flex items-center gap-2 text-slate-700 text-xs cursor-pointer">
                    <Check className={`h-3.5 w-3.5 ${value === 'all' ? 'opacity-100 text-emerald-400' : 'opacity-0'}`} />
                    <span className="text-slate-500 italic">all (no filter)</span>
                  </CommandItem>
                )}
                {filtered.map(s => (
                  <CommandItem
                    key={s}
                    value={s}
                    onSelect={() => { onChange(s); setOpen(false); setSearch(''); }}
                    className="flex items-center gap-2 text-slate-700 text-xs cursor-pointer">
                    <Check className={`h-3.5 w-3.5 ${value === s ? 'opacity-100 text-emerald-400' : 'opacity-0'}`} />
                    <span className="truncate">{s}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}